import React, { useState, useEffect, useCallback } from "react";
import { useArtist } from "../../../context/ArtistContext";
import { useArtistData } from "../../../context/ArtistDataContext";
import DashboardLayout from "../../../components/common/DashboardLayout";
import ConfirmModal from "../components/ConfirmModal";
import { get, put, post, del } from "../../../services/api";
import { validateTimeRange } from "../../../utils/timeValidation";
import "../../../styles/artist.css";

const DAYS_OF_WEEK = [
  { value: 0, label: "Sunday" },
  { value: 1, label: "Monday" },
  { value: 2, label: "Tuesday" },
  { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" },
  { value: 5, label: "Friday" },
  { value: 6, label: "Saturday" },
];

const Availability = () => {
  const { selectedArtist } = useArtist();
  const {
    getAvailability,
    fetchAvailability,
    updateAvailabilityCache,
    invalidateCache,
  } = useArtistData();
  // Legacy support: use selectedArtist as artistProfile for compatibility
  const artistProfile = selectedArtist;
  // Check if current user is the owner of this artist
  const isOwner = selectedArtist?.role === "owner";
  const [weeklyAvailability, setWeeklyAvailability] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Edit mode state
  const [editingDays, setEditingDays] = useState({});
  const [pendingAvailability, setPendingAvailability] = useState({});
  const [originalAvailability, setOriginalAvailability] = useState({});

  // Delete confirmation modal state
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Block form state
  const [showBlockForm, setShowBlockForm] = useState(false);
  const [blockForm, setBlockForm] = useState({
    start_date: "",
    end_date: "",
    note: "",
  });

  // Helper to refresh availability after updates
  const refreshAvailability = useCallback(() => {
    if (artistProfile?.artist_id) {
      invalidateCache("availability", artistProfile.artist_id);
      fetchAvailability(artistProfile.artist_id).then(() => {
        const updatedCache = getAvailability(artistProfile.artist_id);
        setWeeklyAvailability(updatedCache.weekly || []);
        setBlocks(updatedCache.blocks || []);
      });
    }
  }, [
    artistProfile?.artist_id,
    fetchAvailability,
    getAvailability,
    invalidateCache,
  ]);

  // Helper to process availability data
  const processAvailabilityData = useCallback((weeklyData, blocksData) => {
    // Process weekly availability to match expected format
    // Handle both string and number day_of_week values
    const availabilityMap = {};
    (weeklyData || []).forEach((avail) => {
      // Normalize day_of_week to number for consistent matching
      const dayOfWeek =
        typeof avail.day_of_week === "string"
          ? parseInt(avail.day_of_week, 10)
          : avail.day_of_week;

      // Only add to map if day_of_week is valid
      if (!isNaN(dayOfWeek) && dayOfWeek >= 0 && dayOfWeek <= 6) {
        // Store the actual data from database - preserve exact values
        availabilityMap[dayOfWeek] = {
          day_of_week: dayOfWeek,
          start_time: avail.start_time, // Use actual value, no default fallback
          end_time: avail.end_time, // Use actual value, no default fallback
          is_available:
            avail.is_available !== undefined ? avail.is_available : true,
        };
      }
    });

    // Fill in missing days with defaults
    const fullAvailability = DAYS_OF_WEEK.map((day) => {
      // Check both number and string versions to handle any type mismatches
      const existing =
        availabilityMap[day.value] || availabilityMap[String(day.value)];
      if (existing) {
        // Use the actual data from the database - preserve exact values
        return {
          day_of_week: existing.day_of_week,
          start_time: existing.start_time, // Use actual value from DB
          end_time: existing.end_time, // Use actual value from DB - this is the key fix
          is_available:
            existing.is_available !== undefined ? existing.is_available : true,
        };
      }
      // Only use default if day truly doesn't exist in data
      return {
        day_of_week: day.value,
        start_time: "07:00:00",
        end_time: "03:00:00",
        is_available: true,
      };
    });

    setWeeklyAvailability(fullAvailability);
    setBlocks(blocksData || []);
  }, []);

  // Load data on mount and when artist changes
  // Only load if a specific artist is selected (not "all")
  // Use cached data from ArtistDataContext
  useEffect(() => {
    if (!artistProfile || !artistProfile.artist_id) {
      setLoading(false);
      setWeeklyAvailability([]);
      setBlocks([]);
      return;
    }

    const artistId = artistProfile.artist_id;
    const cached = getAvailability(artistId);

    // If we have cached data that's already been fetched, use it
    if (cached.hasFetched && !cached.loading) {
      // Process the cached data (even if empty - means artist has no availability set)
      processAvailabilityData(cached.weekly || [], cached.blocks || []);
      setLoading(false);
      setError(cached.error || null);
      return;
    }

    // If cache is loading, wait for it
    if (cached.loading) {
      setLoading(true);
      return;
    }

    // Otherwise, fetch the data
    setLoading(true);
    fetchAvailability(artistId)
      .then(() => {
        const updatedCache = getAvailability(artistId);
        // Only process after fetch completes - this ensures we have confirmed data
        processAvailabilityData(
          updatedCache.weekly || [],
          updatedCache.blocks || [],
        );
        setLoading(false);
        setError(updatedCache.error || null);
      })
      .catch((err) => {
        console.error("Error loading availability:", err);
        setLoading(false);
        setError(err.message);
      });
  }, [
    artistProfile?.artist_id,
    getAvailability,
    fetchAvailability,
    processAvailabilityData,
  ]);

  // Convert time from HH:MM:SS to HH:MM for input
  const formatTimeForInput = (time) => {
    if (!time) return "";
    return time.substring(0, 5); // "HH:MM:SS" -> "HH:MM"
  };

  // Convert time from HH:MM to HH:MM:SS for API
  const formatTimeForAPI = (time) => {
    if (!time) return "00:00:00";
    return time.length === 5 ? `${time}:00` : time;
  };

  // Update weekly availability for a day
  const updateDayAvailability = async (dayOfWeek, updates) => {
    try {
      setSaving({ ...saving, [dayOfWeek]: true });
      setError(null);
      setSuccessMessage(null);

      const artistId = artistProfile?.artist_id;
      const formattedUpdates = {
        start_time: formatTimeForAPI(updates.start_time),
        end_time: formatTimeForAPI(updates.end_time),
        is_available:
          updates.is_available !== undefined ? updates.is_available : true,
        ...(artistId ? { artistId } : {}),
      };

      const data = await put(
        `/availability/weekly/${dayOfWeek}`,
        formattedUpdates,
      );
      if (data.success && data.availability) {
        // Ensure day_of_week is a number for consistency
        const normalizedDayOfWeek =
          typeof dayOfWeek === "string" ? parseInt(dayOfWeek, 10) : dayOfWeek;

        // Update local state with the response data (which has the correct format from DB)
        // Make sure we're using the exact data from the API response
        const updatedAvailability = weeklyAvailability.map((avail) => {
          const availDay =
            typeof avail.day_of_week === "string"
              ? parseInt(avail.day_of_week, 10)
              : avail.day_of_week;

          if (availDay === normalizedDayOfWeek) {
            // Use the exact data from API response, ensuring all fields are present
            return {
              ...avail,
              ...data.availability,
              day_of_week: normalizedDayOfWeek, // Ensure day_of_week is a number
              start_time: data.availability.start_time || avail.start_time,
              end_time: data.availability.end_time || avail.end_time,
              is_available:
                data.availability.is_available !== undefined
                  ? data.availability.is_available
                  : avail.is_available,
            };
          }
          return avail;
        });
        setWeeklyAvailability(updatedAvailability);

        // Update cache immediately with the updated data from the API response
        // This ensures the cache has the correct data from the database
        if (artistProfile?.artist_id && data.availability) {
          // Ensure the day_of_week is set correctly in the cache update
          // Use the exact data from the API response - don't modify it
          const cacheUpdate = {
            ...data.availability,
            day_of_week: normalizedDayOfWeek, // Ensure it's a number
            // Preserve the exact times from the API response
            start_time: data.availability.start_time,
            end_time: data.availability.end_time,
          };
          updateAvailabilityCache(artistProfile.artist_id, cacheUpdate);
        }
        setSuccessMessage("Availability updated successfully!");
        setTimeout(() => setSuccessMessage(null), 3000);
        return true;
      }
      return false;
    } catch (err) {
      console.error("Error updating availability:", err);
      setError(err.message);
      return false;
    } finally {
      setSaving({ ...saving, [dayOfWeek]: false });
    }
  };

  // Enter edit mode for a day
  const handleEditDay = (dayOfWeek) => {
    if (!isOwner) return; // Only owners can edit
    const day = weeklyAvailability.find((a) => a.day_of_week === dayOfWeek);
    if (!day) return;

    // Store original values
    setOriginalAvailability((prev) => ({
      ...prev,
      [dayOfWeek]: { ...day },
    }));

    // Initialize pending values with current values
    setPendingAvailability((prev) => ({
      ...prev,
      [dayOfWeek]: { ...day },
    }));

    // Enter edit mode
    setEditingDays((prev) => ({
      ...prev,
      [dayOfWeek]: true,
    }));
  };

  // Handle pending changes in edit mode
  const handlePendingChange = (dayOfWeek, field, value) => {
    setPendingAvailability((prev) => ({
      ...prev,
      [dayOfWeek]: {
        ...prev[dayOfWeek],
        [field]: value,
      },
    }));
  };

  // Save changes for a day
  const handleSaveDay = async (dayOfWeek) => {
    const pending = pendingAvailability[dayOfWeek];
    if (!pending) return;

    // Validate times if available
    if (pending.is_available && pending.start_time && pending.end_time) {
      const validation = validateTimeRange(
        pending.start_time,
        pending.end_time,
      );
      if (!validation.isValid) {
        setError(validation.error);
        return;
      }
      if (validation.warning) {
        // Show warning but allow save
        setSuccessMessage(validation.warning);
        setTimeout(() => setSuccessMessage(null), 5000);
      }
    }

    const formattedUpdates = {
      start_time: formatTimeForAPI(pending.start_time),
      end_time: formatTimeForAPI(pending.end_time),
      is_available:
        pending.is_available !== undefined ? pending.is_available : true,
    };

    const success = await updateDayAvailability(dayOfWeek, formattedUpdates);

    // Only exit edit mode on success
    if (success) {
      setEditingDays((prev) => {
        const newState = { ...prev };
        delete newState[dayOfWeek];
        return newState;
      });

      setPendingAvailability((prev) => {
        const newState = { ...prev };
        delete newState[dayOfWeek];
        return newState;
      });

      setOriginalAvailability((prev) => {
        const newState = { ...prev };
        delete newState[dayOfWeek];
        return newState;
      });
    }
  };

  // Cancel changes for a day
  const handleCancelDay = (dayOfWeek) => {
    // Restore original values
    const original = originalAvailability[dayOfWeek];
    if (original) {
      setWeeklyAvailability((prev) =>
        prev.map((avail) =>
          avail.day_of_week === dayOfWeek ? original : avail,
        ),
      );
    }

    // Exit edit mode
    setEditingDays((prev) => {
      const newState = { ...prev };
      delete newState[dayOfWeek];
      return newState;
    });

    setPendingAvailability((prev) => {
      const newState = { ...prev };
      delete newState[dayOfWeek];
      return newState;
    });

    setOriginalAvailability((prev) => {
      const newState = { ...prev };
      delete newState[dayOfWeek];
      return newState;
    });
  };

  // Handle toggle availability (only works when not in edit mode)
  const handleToggleAvailability = async (dayOfWeek) => {
    if (!isOwner) return; // Only owners can toggle
    // If in edit mode, just update pending state
    if (editingDays[dayOfWeek]) {
      const pending = pendingAvailability[dayOfWeek];
      if (pending) {
        handlePendingChange(dayOfWeek, "is_available", !pending.is_available);
      }
      return;
    }

    // Otherwise, save immediately (toggle is a simple action)
    const day = weeklyAvailability.find((a) => a.day_of_week === dayOfWeek);
    if (!day) return;

    const updates = {
      ...day,
      is_available: !day.is_available,
    };

    await updateDayAvailability(dayOfWeek, updates);
  };

  // Handle block form change
  const handleBlockFormChange = (e) => {
    const { name, value } = e.target;
    setBlockForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Create new block
  const handleCreateBlock = async (e) => {
    e.preventDefault();
    if (!isOwner) {
      setError("Only the owner can create availability blocks");
      return;
    }
    try {
      setError(null);
      setSuccessMessage(null);

      if (!blockForm.start_date || !blockForm.end_date) {
        setError("Please select both start and end dates");
        return;
      }

      const artistId = artistProfile?.artist_id;
      const blockData = {
        ...blockForm,
        ...(artistId ? { artistId } : {}),
      };
      const data = await post("/availability/blocks", blockData);
      if (data.success) {
        setBlocks((prev) =>
          [...prev, data.block].sort(
            (a, b) => new Date(a.start_date) - new Date(b.start_date),
          ),
        );
        // Invalidate cache to force refresh on next load
        if (artistProfile?.artist_id) {
          invalidateCache("availability", artistProfile.artist_id);
        }
        setBlockForm({ start_date: "", end_date: "", note: "" });
        setShowBlockForm(false);
        setSuccessMessage("Block created successfully!");
        setTimeout(() => setSuccessMessage(null), 3000);
      }
    } catch (err) {
      console.error("Error creating block:", err);
      setError(err.message);
    }
  };

  // Delete block
  const handleDeleteBlockClick = (blockId) => {
    if (!isOwner) return; // Only owners can delete
    setDeleteConfirm(blockId);
  };

  const handleDeleteBlock = async () => {
    if (!deleteConfirm) return;

    try {
      setIsDeleting(true);
      setError(null);
      const data = await del(`/availability/blocks/${deleteConfirm}`);
      if (data.success) {
        setBlocks((prev) => prev.filter((b) => b.block_id !== deleteConfirm));
        // Invalidate cache to force refresh on next load
        if (artistProfile?.artist_id) {
          invalidateCache("availability", artistProfile.artist_id);
        }
        setSuccessMessage("Block deleted successfully!");
        setTimeout(() => setSuccessMessage(null), 3000);
        setDeleteConfirm(null);
      }
    } catch (err) {
      console.error("Error deleting block:", err);
      setError(err.message);
    } finally {
      setIsDeleting(false);
    }
  };

  // Check if time crosses midnight
  const crossesMidnight = (startTime, endTime) => {
    if (!startTime || !endTime) return false;
    const start = startTime.substring(0, 5);
    const end = endTime.substring(0, 5);
    return end < start;
  };

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Check if "all" is selected - block editing
  const isAllSelected = selectedArtist === null;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="artist-dashboard">
          <div
            style={{
              textAlign: "center",
              padding: "3rem",
              color: "var(--text-muted)",
            }}
          >
            Loading availability...
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // Show message when "all" is selected
  if (isAllSelected) {
    return (
      <DashboardLayout>
        <div className="artist-dashboard">
          <h1 className="section-title">Availability Settings</h1>
          <div
            style={{
              textAlign: "center",
              padding: "3rem",
              color: "var(--text-muted)",
              fontSize: "1.1rem",
              background: "rgba(59, 130, 246, 0.1)",
              border: "1px solid rgba(59, 130, 246, 0.3)",
              borderRadius: "8px",
              marginTop: "2rem",
            }}
          >
            Select a group in the dropdown in the sidebar to adjust availability
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="artist-dashboard">
        <h1 className="section-title">Availability Settings</h1>
        <p
          style={{
            textAlign: "center",
            color: "var(--text-muted)",
            marginBottom: "2rem",
          }}
        >
          Set your weekly schedule and block out dates when you're unavailable
        </p>

        {!isOwner && (
          <div
            style={{
              background: "rgba(251, 191, 36, 0.1)",
              border: "1px solid rgba(251, 191, 36, 0.3)",
              color: "#fbbf24",
              padding: "1rem",
              borderRadius: "8px",
              marginBottom: "1.5rem",
              textAlign: "center",
            }}
          >
            Only the owner of this group can edit availability.
          </div>
        )}

        {error && (
          <div
            style={{
              background: "rgba(250, 82, 82, 0.1)",
              border: "1px solid var(--border-red)",
              color: "var(--primary-red)",
              padding: "1rem",
              borderRadius: "8px",
              marginBottom: "1.5rem",
              textAlign: "center",
            }}
          >
            {error}
          </div>
        )}

        {successMessage && (
          <div
            style={{
              background: "rgba(34, 197, 94, 0.1)",
              border: "1px solid rgba(34, 197, 94, 0.3)",
              color: "#22c55e",
              padding: "1rem",
              borderRadius: "8px",
              marginBottom: "1.5rem",
              textAlign: "center",
            }}
          >
            {successMessage}
          </div>
        )}

        {/* Weekly Schedule Section */}
        <div className="availability-section" style={{ marginBottom: "3rem" }}>
          <h2
            style={{
              color: "var(--text-white)",
              marginBottom: "1.5rem",
              fontSize: "1.5rem",
            }}
          >
            Weekly Schedule
          </h2>
          <div className="weekly-schedule-grid">
            {weeklyAvailability.map((day) => {
              const dayInfo = DAYS_OF_WEEK.find(
                (d) => d.value === day.day_of_week,
              );
              const isSaving = saving[day.day_of_week];
              const isEditing = editingDays[day.day_of_week];
              const pending = pendingAvailability[day.day_of_week];

              // Use pending values if in edit mode, otherwise use current values
              const displayDay = isEditing && pending ? pending : day;
              const crossesMidnightFlag = crossesMidnight(
                displayDay.start_time,
                displayDay.end_time,
              );

              return (
                <div
                  key={day.day_of_week}
                  className="availability-day-card"
                  style={{
                    opacity: displayDay.is_available ? 1 : 0.6,
                    position: "relative",
                  }}
                >
                  <div className="availability-day-header">
                    <h3 style={{ color: "var(--text-white)", margin: 0 }}>
                      {dayInfo?.label}
                    </h3>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={displayDay.is_available}
                        onChange={() =>
                          handleToggleAvailability(day.day_of_week)
                        }
                        disabled={isSaving || !isOwner}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>

                  {displayDay.is_available && (
                    <div className="availability-time-inputs">
                      <div className="time-input-group">
                        <label
                          style={{
                            color: "var(--text-muted)",
                            fontSize: "0.85rem",
                          }}
                        >
                          Start Time
                        </label>
                        <input
                          type="time"
                          value={formatTimeForInput(displayDay.start_time)}
                          onChange={(e) =>
                            handlePendingChange(
                              day.day_of_week,
                              "start_time",
                              e.target.value,
                            )
                          }
                          className="settings-input"
                          disabled={isSaving || !isEditing || !isOwner}
                        />
                      </div>

                      <div className="time-input-group">
                        <label
                          style={{
                            color: "var(--text-muted)",
                            fontSize: "0.85rem",
                          }}
                        >
                          End Time
                          {crossesMidnightFlag && (
                            <span
                              style={{
                                display: "block",
                                fontSize: "0.7rem",
                                color: "var(--primary-red)",
                                marginTop: "0.25rem",
                              }}
                            >
                              (Next Day)
                            </span>
                          )}
                        </label>
                        <input
                          type="time"
                          value={formatTimeForInput(displayDay.end_time)}
                          onChange={(e) =>
                            handlePendingChange(
                              day.day_of_week,
                              "end_time",
                              e.target.value,
                            )
                          }
                          className="settings-input"
                          disabled={isSaving || !isEditing || !isOwner}
                        />
                      </div>
                    </div>
                  )}

                  {!displayDay.is_available && (
                    <div
                      style={{
                        color: "var(--text-muted)",
                        fontSize: "0.9rem",
                        textAlign: "center",
                        padding: "1rem 0",
                      }}
                    >
                      Unavailable
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div
                    style={{
                      display: "flex",
                      gap: "0.5rem",
                      marginTop: "1rem",
                      justifyContent: "flex-end",
                    }}
                  >
                    {!isEditing ? (
                      <button
                        onClick={() => handleEditDay(day.day_of_week)}
                        className="submit-btn"
                        style={{
                          padding: "0.5rem 1rem",
                          fontSize: "0.85rem",
                          background: "rgba(59, 130, 246, 0.1)",
                          border: "1px solid rgba(59, 130, 246, 0.3)",
                          color: "#3b82f6",
                        }}
                        disabled={isSaving || !isOwner}
                      >
                        Edit
                      </button>
                    ) : (
                      <>
                        <button
                          onClick={() => handleCancelDay(day.day_of_week)}
                          className="submit-btn"
                          style={{
                            padding: "0.5rem 1rem",
                            fontSize: "0.85rem",
                            background: "rgba(107, 114, 128, 0.1)",
                            border: "1px solid rgba(107, 114, 128, 0.3)",
                            color: "#6b7280",
                          }}
                          disabled={isSaving}
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => handleSaveDay(day.day_of_week)}
                          className="submit-btn"
                          style={{
                            padding: "0.5rem 1rem",
                            fontSize: "0.85rem",
                          }}
                          disabled={isSaving}
                        >
                          {isSaving ? "Saving..." : "Save"}
                        </button>
                      </>
                    )}
                  </div>

                  {isSaving && (
                    <div
                      className="availability-saving-overlay"
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "rgba(0, 0, 0, 0.3)",
                        borderRadius: "8px",
                        color: "var(--text-white)",
                        fontSize: "0.8rem",
                        zIndex: 1,
                      }}
                    >
                      Saving...
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Date Blocks Section */}
        <div className="availability-section">
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "1.5rem",
            }}
          >
            <h2
              style={{
                color: "var(--text-white)",
                margin: 0,
                fontSize: "1.5rem",
              }}
            >
              Blocked Dates
            </h2>
            <button
              onClick={() => setShowBlockForm(!showBlockForm)}
              className="submit-btn"
              style={{ padding: "0.5rem 1.5rem", fontSize: "0.9rem" }}
              disabled={!isOwner}
            >
              {showBlockForm ? "Cancel" : "+ Add Block"}
            </button>
          </div>

          {showBlockForm && (
            <form onSubmit={handleCreateBlock} className="block-form">
              <div className="form-grid">
                <div className="input-group">
                  <label className="input-label">Start Date *</label>
                  <input
                    type="date"
                    name="start_date"
                    value={blockForm.start_date}
                    onChange={handleBlockFormChange}
                    className="settings-input"
                    required
                  />
                </div>

                <div className="input-group">
                  <label className="input-label">End Date *</label>
                  <input
                    type="date"
                    name="end_date"
                    value={blockForm.end_date}
                    onChange={handleBlockFormChange}
                    className="settings-input"
                    required
                    min={blockForm.start_date || undefined}
                  />
                </div>

                <div className="input-group full-width">
                  <label className="input-label">Note (Optional)</label>
                  <input
                    type="text"
                    name="note"
                    value={blockForm.note}
                    onChange={handleBlockFormChange}
                    className="settings-input"
                    placeholder="e.g., Vacation, Personal time"
                    maxLength={255}
                  />
                </div>
              </div>

              <button
                type="submit"
                className="submit-btn"
                style={{ marginTop: "1rem" }}
              >
                Create Block
              </button>
            </form>
          )}

          {blocks.length === 0 ? (
            <div
              style={{
                textAlign: "center",
                padding: "2rem",
                color: "var(--text-muted)",
                fontSize: "0.9rem",
              }}
            >
              No blocked dates. Click "Add Block" to block out dates when you're
              unavailable.
            </div>
          ) : (
            <div className="blocks-list">
              {blocks.map((block) => (
                <div key={block.block_id} className="block-card">
                  <div className="block-card-content">
                    <div>
                      <div
                        style={{ color: "var(--text-white)", fontWeight: 600 }}
                      >
                        {formatDate(block.start_date)}
                        {block.start_date !== block.end_date && (
                          <> - {formatDate(block.end_date)}</>
                        )}
                      </div>
                      {block.note && (
                        <div
                          style={{
                            color: "var(--text-muted)",
                            fontSize: "0.85rem",
                            marginTop: "0.25rem",
                          }}
                        >
                          {block.note}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => handleDeleteBlockClick(block.block_id)}
                      className="delete-btn"
                      style={{
                        background: "rgba(250, 82, 82, 0.1)",
                        border: "1px solid var(--border-red)",
                        color: "var(--primary-red)",
                        padding: "0.5rem 1rem",
                        borderRadius: "6px",
                        cursor: isOwner ? "pointer" : "not-allowed",
                        fontSize: "0.85rem",
                        opacity: isOwner ? 1 : 0.5,
                      }}
                      disabled={!isOwner}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Delete Confirmation Modal */}
        <ConfirmModal
          open={deleteConfirm !== null}
          title="Delete Block"
          message="Are you sure you want to delete this availability block? This action cannot be undone."
          confirmLabel="Delete"
          cancelLabel="Cancel"
          isConfirming={isDeleting}
          onConfirm={handleDeleteBlock}
          onCancel={() => setDeleteConfirm(null)}
        />
      </div>
    </DashboardLayout>
  );
};

export default Availability;
